import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class QuantityBreaks extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#setupEventListeners();
    this.#syncQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    }
  }

  #setupEventListeners() {
    const cards = this.querySelectorAll('.qb-card');
    cards.forEach(card => {
      // Setup click selection
      card.addEventListener('click', () => {
        cards.forEach(c => {
          c.classList.remove('active');
          c.setAttribute('aria-checked', 'false');
        });
        card.classList.add('active');
        card.setAttribute('aria-checked', 'true');
        this.#syncQuantity();
      });

      // Handle keyboard navigation (Space/Enter)
      card.addEventListener('keydown', (event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          card.click();
        }
      });
    });

    // Listen to variant changes
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    }
  }

  #syncQuantity() {
    const activeCard = this.querySelector('.qb-card.active');
    if (!activeCard) return;
    const quantity = parseInt(activeCard.dataset.quantity) || 1;
    const giftVariantId = activeCard.dataset.giftVariantId;

    // Find the product form
    const productComponent = this.closest('product-component');
    if (productComponent) {
      const form = productComponent.querySelector('form[data-type="add-to-cart-form"]');
      if (form) {
        // Find the main variant ID input in the form (unlinked to items array)
        const mainIdInput = form.querySelector('input[name="id"]:not([name*="items"])');
        if (!mainIdInput) return;

        const mainVariantId = mainIdInput.value;

        // Clear any previously injected bundle inputs from this block
        form.querySelectorAll('.qb-injected-input').forEach(el => el.remove());

        if (giftVariantId && giftVariantId !== '') {
          // 1. Disable standard inputs so they aren't submitted
          mainIdInput.disabled = true;

          const qtyInputs = form.querySelectorAll('input[name="quantity"]:not([name*="items"])');
          qtyInputs.forEach(input => {
            input.disabled = true;

            // Hide standard quantity inputs so they don't look duplicate
            const wrapper = input.closest('.quantity-selector-wrapper');
            if (wrapper) {
              wrapper.style.display = 'none';
            }
          });

          // 2. Inject dynamic item array inputs
          const bundleId = 'bundle_' + Date.now();

          const injectInput = (name, value) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.className = 'qb-injected-input';
            input.name = name;
            input.value = value;
            form.appendChild(input);
          };

          // Item 0: Main Product
          injectInput('items[0][id]', mainVariantId);
          injectInput('items[0][quantity]', quantity.toString());
          injectInput('items[0][properties][_bundle_id]', bundleId);
          injectInput('items[0][properties][_bundle_role]', 'main');

          // Item 1: Free Gift Product
          injectInput('items[1][id]', giftVariantId);
          injectInput('items[1][quantity]', '1');
          injectInput('items[1][properties][_bundle_id]', bundleId);
          injectInput('items[1][properties][_bundle_role]', 'free');

        } else {
          // Restore standard form submission behavior
          mainIdInput.disabled = false;

          const qtyInputs = form.querySelectorAll('input[name="quantity"]:not([name*="items"])');
          if (qtyInputs.length > 0) {
            qtyInputs.forEach(input => {
              input.disabled = false;
              input.value = quantity;
              input.dispatchEvent(new Event('change', { bubbles: true }));
              input.dispatchEvent(new Event('input', { bubbles: true }));

              // Hide standard quantity inputs so they don't look duplicate
              const wrapper = input.closest('.quantity-selector-wrapper');
              if (wrapper) {
                wrapper.style.display = 'none';
              }
            });
          } else {
            // If no quantity input is in the form, create/update a hidden one
            let hiddenInput = form.querySelector('input[name="quantity"][type="hidden"]');
            if (!hiddenInput) {
              hiddenInput = document.createElement('input');
              hiddenInput.type = 'hidden';
              hiddenInput.name = 'quantity';
              hiddenInput.className = 'qb-injected-input';
              form.appendChild(hiddenInput);
            }
            hiddenInput.value = quantity;
            hiddenInput.disabled = false;
            hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    }
  }

  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const newBlock = detail.html.querySelector(`quantity-breaks[data-block-id="${this.dataset.blockId}"]`);
        if (newBlock) {
          this.innerHTML = newBlock.innerHTML;
          // Re-bind click event listeners and sync quantity
          this.#setupEventListeners();
          this.#syncQuantity();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quantity-breaks] Variant pricing update failed:', error);
      });
  };
}

if (!customElements.get('quantity-breaks')) {
  customElements.define('quantity-breaks', QuantityBreaks);
}
